import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Form,
  Input,
  Button,
  Row,
  Col,
  Spin,
  Card,
  message,
  DatePicker,
} from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { onError } from 'utils/handlers';
import Table from 'components/Table';
import FormSubmitControls from 'components/FormSubmitControls';
import ModalDelete from 'components/ModalDelete';
import { isMutable, isAuthorized } from 'utils/estadosGlobales';
import Uploader from 'components/Uploader';
import Previewer from 'components/Previewer';
import {
  deleteDocumentoVehicular,
  getDocumentosVehiculares,
  patchDocumentoVehicular,
  postDocumentoVehicular,
} from 'api/recaudacion/documentosVehiculares';
import { DATE_FRONTEND_FORMAT } from 'utils/formatValues';

export const model = 'documentodetramite';
export const permissionConfig = { model };

const DocumentosVehiculares = ({
  vehiculoId,
  permission,
}) => {
  const [form] = Form.useForm();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [visibleAlert, setVisibleAlert] = useState(false);
  const [data, setData] = useState([]);
  const [currentFile, setCurrentFile] = useState();
  const [visiblePreview, setVisiblePreview] = useState(false);

  const fetchData = async () => {
    setLoading(true);
    const _data = await getDocumentosVehiculares(vehiculoId);
    setData(_data);
    setLoading(false);
  };

  useEffect(() => {
    let mounted = true;
    const _fetchData = async () => {
      setLoading(true);
      const _data = await getDocumentosVehiculares(vehiculoId);
      if (mounted) {
        setData(_data);
        setLoading(false);
      }
    };
    if (vehiculoId) {
      _fetchData();
    }
    return () => { mounted = false; };
    // eslint-disable-next-line
  }, []);

  const onCancel = () => {
    setVisible(false);
    form.resetFields();
    setSelectedRowKeys([]);
  };

  const onFinish = async () => {
    try {
      setLoading(true);
      await form.validateFields();
      const values = form.getFieldsValue();
      const [key] = selectedRowKeys;
      const saved = await (key
        ? patchDocumentoVehicular(vehiculoId, values)
        : postDocumentoVehicular(vehiculoId, values));
      if (saved) {
        onCancel();
        await fetchData();
      }
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const deleteItem = async () => {
    setLoading(true);
    const [key] = selectedRowKeys;
    const deleted = await deleteDocumentoVehicular(key, vehiculoId);
    if (deleted) {
      setVisibleAlert(false);
      onCancel();
      await fetchData();
    }
    setLoading(false);
  };

  const handleOnRowClick = (record) => {
    setSelectedRowKeys([record.id]);
    form.setFieldsValue(record);
  };

  const rowSelection = {
    selectedRowKeys,
    type: 'radio',
  };

  const onClickAdd = () => {
    onCancel();
    setVisible(true);
  };

  const onClickEdit = async () => {
    try {
      setLoading(true);
      setVisible(true);
      setLoading(false);
    } catch (err) {
      onError(err, setLoading);
    }
  };

  const onClickDelete = () => {
    const [key] = selectedRowKeys;
    const match = data.find((e) => e.id === key);
    if (isMutable(match)) {
      setVisibleAlert(true);
    } else {
      message.warn(`No se puede eliminar un registro ${isAuthorized(match) ? 'autorizado' : 'cancelado'}`);
    }
  };

  const rules = {
    required: [{ required: true }],
  };

  const columns = [
    {
      titleText: 'Descripción',
      dataIndex: 'descripcion',
      key: 'descripcion',
      width: 200,
    },
    {
      titleText: 'Fecha de Vencimiento',
      dataIndex: 'fecha_vencimiento',
      key: 'fecha_vencimiento',
      width: 200,
      render: (val) => (val?.format(DATE_FRONTEND_FORMAT)),
    },
  ];

  const onClickPreview = () => {
    const clone = [...data];
    const [key] = selectedRowKeys;
    const match = clone.find((e) => e.id === key);
    setCurrentFile(match);
    setVisiblePreview(true);
  };

  return (
    <Row className="container">
      <Spin tip="Cargando..." spinning={loading}>
        {!visible ? (
          <Table
            cols={columns}
            data={data}
            rowSelection={rowSelection}
            handleOnRowClick={handleOnRowClick}
            controls={{
              onClickAdd,
              onClickEdit,
              onClickDelete,
            }}
            mobileColIndex={0}
            customActions={[
              {
                onClick: onClickPreview,
                icon: <EyeOutlined />,
                text: 'Ver Foto',
                disabled: !!selectedRowKeys.length,
              },
            ]}
            permission={permission}
          />
        ) : (
          <Card
            className="form-container"
            title={`${selectedRowKeys.length ? 'Editar' : 'Agregar'} Documento`}
            extra={(
              <FormSubmitControls
                onFinish={onFinish}
                onCancel={onCancel}
                loading={loading}
                permission={permission}
              />
            )}
            bordered={false}
          >
            <Form
              layout="vertical"
              form={form}
              onFinish={() => onFinish()}
              scrollToFirstError
            >
              <Row gutter={10}>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="clave"
                    rules={rules.required}
                    label="Clave"
                    hasFeedback
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="descripcion"
                    rules={rules.required}
                    label="Descripción"
                    hasFeedback
                  >
                    <Input allowClear />
                  </Form.Item>
                </Col>
                <Col xs={24} sm={24} md={8}>
                  <Form.Item
                    name="fecha_vencimiento"
                    label="Fecha de Vencimiento"
                  >
                    <DatePicker placeholder="" format="DD/MM/YYYY" />
                  </Form.Item>
                </Col>
                <Uploader
                  title="Foto"
                  limitMB={5}
                  file={form.getFieldValue('archivo')}
                  formItemName="archivo"
                  allowedExtensions={['jpg', 'png', 'jpeg']}
                  rules={rules.required}
                  allowPreview={false}
                />
              </Row>
              <Form.Item name="id" hidden>
                <Input />
              </Form.Item>
              <Form.Item hidden>
                <Button htmlType="submit" />
              </Form.Item>
            </Form>
          </Card>
        )}
        <ModalDelete
          onDelete={deleteItem}
          onCancel={() => {
            setVisibleAlert(false);
            setSelectedRowKeys([]);
          }}
          visible={visibleAlert}
          content="documento"
          loading={loading}
        />
        <Previewer
          visible={visiblePreview}
          setVisible={setVisiblePreview}
          iFrameUrl={currentFile?.archivo}
          isImage
        />
      </Spin>
    </Row>
  );
};

DocumentosVehiculares.propTypes = {
  vehiculoId: PropTypes.number,
  permission: PropTypes.shape({
    add: PropTypes.bool.isRequired,
    authorize: PropTypes.bool.isRequired,
    cancel: PropTypes.bool.isRequired,
    change: PropTypes.bool.isRequired,
    delete: PropTypes.bool.isRequired,
    export: PropTypes.bool.isRequired,
    import: PropTypes.bool.isRequired,
    sync: PropTypes.bool.isRequired,
    view: PropTypes.bool.isRequired,
  }).isRequired,
};

DocumentosVehiculares.defaultProps = {
  vehiculoId: null,
};

export default DocumentosVehiculares;
